/**
 * Core API instance with interceptors
 */
import axios from "axios";
import { API_SETTINGS, ERROR_MESSAGES, HTTP_STATUS } from "../utils/api";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8787/api";

// -------------------------------------------------------
// Core API Instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: API_SETTINGS?.TIMEOUT || 30000,
});

// -------------------------------------------------------
// Logging & interceptors
const logRequest = (prefix) => (config) => {
  const method = config.method?.toUpperCase();
  console.log(`🟢 ${prefix}: ${method} ${config.url}`);

  if (config.params && Object.keys(config.params).length > 0) {
    console.log(`📥 ${prefix} Params:`, config.params);
  }

  if (config.data) {
    console.log(`📤 ${prefix} Data:`, config.data);
  }

  return config;
};

api.interceptors.request.use(logRequest("API"));

const handleResponseError = (prefix) => (error) => {
  if (error.response) {
    console.error(`🔴 ${prefix} Error [${error.response.status}]`, {
      url: error.config?.url,
      data: error.response.data,
    });
  } else if (error.request) {
    console.error(`🔴 ${prefix} Network Error`, { url: error.config?.url });
  } else {
    console.error(`🔴 ${prefix} Request Error:`, error.message);
  }
  return Promise.reject(handleApiError(error));
};

api.interceptors.response.use((response) => {
  // Extra info
  const { status, statusText, headers, config, data } = response;

  console.log("🟢 API Response:");
  console.log("URL:", config.url);
  console.log("Method:", config.method?.toUpperCase());
  console.log("Status:", status, statusText);
  console.log("Headers:", headers);
  console.log("Data:", data);

  // Tu peux enrichir la réponse avant de la renvoyer
  return {
    ...response,
  };
}, handleResponseError("API"));

// -------------------------------------------------------
// Unified error handler
const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const message =
      data?.error ||
      data?.message ||
      ERROR_MESSAGES?.UNKNOWN ||
      "Erreur inconnue";

    const statusMessages = {
      [HTTP_STATUS.BAD_REQUEST]: `${message} (Requête invalide)`,
      [HTTP_STATUS.UNAUTHORIZED]: `${message} (Non autorisé)`,
      [HTTP_STATUS.FORBIDDEN]: `${message} (Accès refusé)`,
      [HTTP_STATUS.NOT_FOUND]: `${message} (Non trouvé)`,
      [HTTP_STATUS.INTERNAL_SERVER_ERROR]: `${message} (Erreur serveur)`,
    };

    const errorMessage = statusMessages[status] || `${message} (${status})`;
    const customError = new Error(errorMessage);
    customError.response = error.response;
    customError.status = status;
    return customError;
  }

  if (error.request)
    return new Error(ERROR_MESSAGES?.NETWORK || "Erreur réseau");
  return new Error(
    error.message || ERROR_MESSAGES?.UNKNOWN || "Erreur inconnue",
  );
};

// -------------------------------------------------------
// CRUD operations factory
export const createCrudOperations = (resource) => ({
  getAll: async (params = {}) =>
    (await api.get(`/${resource}`, { params })).data,
  getById: async (id) => (await api.get(`/${resource}/${id}`)).data,
  create: async (data) => (await api.post(`/${resource}`, data)).data,
  update: async (id, data) => (await api.put(`/${resource}/${id}`, data)).data,
  delete: async (id) => (await api.delete(`/${resource}/${id}`)).data,
});

export default api;
